import React, { useState, useEffect } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { links, profilepic } from '../../common/links';
import { useUserAuth } from '../context/UserAuthContext';
import { PiListBold, PiXBold, PiUserCircleDuotone, PiSignOutDuotone, PiGearSixDuotone, PiMagnifyingGlassDuotone } from 'react-icons/pi';

function Navbar() {
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [open, setOpen] = useState(false);
  const [localSearch, setLocalSearch] = useState("");
  const location = useLocation();
  const navigate = useNavigate();
  const { user, logOut } = useUserAuth();

  useEffect(() => {
    // Sync local search with URL when navigating
    const params = new URLSearchParams(location.search);
    const query = params.get('q') || "";
    setLocalSearch(query);
  }, [location.search]);

  const handleSearch = (e) => {
    const value = e.target.value;
    setLocalSearch(value);
    
    // If we're not on My Space, navigate there with the search query
    if (!location.pathname.includes('myspace')) {
      navigate(`/dashboard/myspace?q=${encodeURIComponent(value)}`);
    } else {
      // If we are on My Space, just update the URL without adding to history stack
      navigate(`?q=${encodeURIComponent(value)}`, { replace: true });
    }
  };

  const handleLogout = async () => {
    try {
      await logOut();
      navigate('/login');
    } catch (error) {
      console.error(error);
    }
  };

  return (
    <nav className={`bg-gray-800  shadow-xl h-16 sticky top-0 z-40 transition-all ${
      location.pathname.includes('profile') ? 'hidden' : 'block'
    }`}>
      <div className="h-full mx-auto px-4 sm:px-6 lg:px-10">
        <div className="flex justify-between items-center h-full">
          
          {/* Left: Mobile Menu & Breadcrumb/Title */}
          <div className="flex items-center gap-4">
            <button 
              onClick={() => setOpen(!open)}
              className="lg:hidden p-2 rounded-lg text-gray-300 hover:bg-gray-700 transition-colors"
            >
              {open ? <PiXBold size={24} /> : <PiListBold size={24} />}
            </button>

            <div className="hidden md:flex flex-col">
              <h2 className="text-sm font-bold text-white leading-tight capitalize">
                {location.pathname.split('/').pop()?.replace('-', ' ') || 'Dashboard'}
              </h2>
              <p className="text-[11px] text-gray-400 font-medium whitespace-nowrap">Cloud Point / Dashboard</p>
            </div>
            
            {/* Mobile Logo */}
            <Link to="/home" className="flex md:hidden items-center gap-2">
              <img src={require('../assert/logo.ico')} alt="Logo" className="h-8 w-8" />
              <span className="font-bold text-white text-sm">Cloud Point</span>
            </Link>
          </div>

          {/* Center: Search Bar (Desktop) */}
          <div className="hidden lg:flex flex-1 max-w-md mx-8">
            <div className="relative w-full group">
              <PiMagnifyingGlassDuotone className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 group-focus-within:text-blue-400 transition-colors text-xl" />
              <input 
                type="text" 
                placeholder="Search files, folders..." 
                value={localSearch}
                onChange={handleSearch}
                className="w-full bg-gray-700/50 border border-gray-600 rounded-xl py-2 pl-10 pr-4 text-sm focus:ring-2 focus:ring-blue-500/20 focus:bg-gray-700 transition-all outline-none text-gray-200 placeholder-gray-400 shadow-sm"
              />
            </div>
          </div>

          {/* Right: User Profile (Notification removed) */}
          <div className="flex items-center gap-2 md:gap-4">
            <div className="h-8 w-[px] bg-gray-700 hidden md:block mx-1"></div>

            <div className="relative">
              <button 
                onClick={() => setIsDropdownOpen(!isDropdownOpen)}
                className="flex items-center gap-3 p-1 pr-3 hover:bg-gray-700/50 rounded-xl transition-all border border-transparent shadow-sm"
              >
                <div className="relative h-10 w-10">
                  <img
                    src={user?.photoURL || profilepic()}
                    alt="User"
                    className="h-full w-full rounded-lg object-cover border border-gray-600 shadow-sm"
                  />
                  <div className="absolute -bottom-0.5 -right-0.5 w-3.5 h-3.5 bg-green-500 border-2 border-gray-800 rounded-full"></div>
                </div>
                <div className="hidden md:flex flex-col items-start">
                  <span className="text-sm font-bold text-white leading-none truncate max-w-[160px]">
                    {user?.displayName || 'Cloud User'}
                  </span>
                  <span className="text-[11px] text-blue-400 font-bold mt-1 uppercase tracking-wider">
                    Free Tier
                  </span>
                </div>
              </button>

              {/* Dropdown Menu */}
              {isDropdownOpen && (
                <>
                  <div className="fixed inset-0 z-40" onClick={() => setIsDropdownOpen(false)}></div>
                  <div className="absolute right-0 mt-3 w-56 bg-white rounded-2xl shadow-xl border border-gray-100 py-2 z-50 animate-in fade-in zoom-in duration-100">
                    <div className="px-4 py-3 border-b border-gray-50 mb-1">
                      <p className="text-xs font-semibold text-gray-400 uppercase tracking-wider mb-1">My Account</p>
                      <p className="text-sm font-bold text-gray-800 truncate">{user?.email}</p>
                    </div>
                    
                    <Link to="profile" onClick={() => setIsDropdownOpen(false)} className="flex items-center gap-3 px-4 py-2.5 text-sm text-gray-600 hover:bg-gray-50 hover:text-blue-600 transition-colors">
                      <PiUserCircleDuotone size={18} />
                      <span className="font-medium">My Profile</span>
                    </Link>
                    <Link to="settings" onClick={() => setIsDropdownOpen(false)} className="flex items-center gap-3 px-4 py-2.5 text-sm text-gray-600 hover:bg-gray-50 hover:text-blue-600 transition-colors">
                      <PiGearSixDuotone size={18} />
                      <span className="font-medium">Settings</span>
                    </Link>
                    <div className="h-px bg-gray-50 my-1"></div>
                    <button 
                      onClick={() => { handleLogout(); setIsDropdownOpen(false); }}
                      className="flex items-center gap-3 w-full px-4 py-2.5 text-sm text-red-600 hover:bg-red-50 transition-colors"
                    >
                      <PiSignOutDuotone size={18} />
                      <span className="font-bold">Sign Out</span>
                    </button>
                  </div>
                </>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Mobile Sidebar Overlay */}
      <div className={`fixed inset-0 z-[60] lg:hidden transition-opacity duration-300 ${open ? 'opacity-100 pointer-events-auto' : 'opacity-0 pointer-events-none'}`}>
        <div className="absolute inset-0 bg-gray-900/40 backdrop-blur-sm" onClick={() => setOpen(false)}></div>
        <div className={`absolute top-0 left-0 bottom-0 w-[280px] bg-[#80cde8] shadow-2xl transition-transform duration-300 ease-out transform ${open ? 'translate-x-0' : '-translate-x-full'}`}>
          <div className="p-6">
            <div className="flex items-center justify-between mb-8">
              <div className="flex items-center gap-3">
                <img src={require('../assert/logo.ico')} alt="Logo" className="h-10 w-10" />
                <span className="text-xl font-black text-gray-900 tracking-tight">Cloud Point</span>
              </div>
              <button onClick={() => setOpen(false)} className="p-2 rounded-lg bg-white/20 text-gray-800">
                <PiXBold size={20} />
              </button>
            </div>

            <nav className="flex flex-col gap-2">
              <Link
                to="overview"
                className={`flex items-center gap-3 px-4 py-3 rounded-xl font-bold transition-all ${
                  location.pathname.endsWith('overview') ? 'bg-gray-900 text-white' : 'text-gray-800 hover:bg-white/20'
                }`}
                onClick={() => setOpen(false)}
              >
                <span>Overview</span>
              </Link>
              {links.map((link) => (
                <Link
                  key={link.name}
                  to={link.link}
                  className={`flex items-center gap-3 px-4 py-3 rounded-xl font-bold transition-all ${
                    location.pathname.includes(link.link) ? 'bg-gray-900 text-white' : 'text-gray-800 hover:bg-white/20'
                  }`}
                  onClick={() => setOpen(false)}
                >
                  {link.name}
                </Link>
              ))}
            </nav>
          </div>
        </div>
      </div>
    </nav>
  );
}

export default Navbar;